/*
 * pdp-cro.js — behaviour + analytics for the product-page redesign A/B test
 * (PostHog flag `product-page-redesign`).
 *
 * Loaded on every product page by snippets/pdp-cro-config.liquid, which has
 * already resolved the flag and stamped the result on <html>:
 *
 *     html.pdp--variant                 -> this visitor is in the test arm
 *     html[data-pdp-variant="..."]      -> the resolved arm ("control" | "redesign")
 *     html[data-pdp-definitive="true"]  -> that arm came from PostHog or a forced
 *                                          override, not the 500ms fallback
 *
 * This file NEVER decides the arm and NEVER swaps DOM nodes — the server
 * renders both states and CSS picks one. Every variant control is a PROXY for
 * a real theme control that stays in the DOM (hidden under the variant):
 *
 *     pack-size cards      -> the native pill radios in <variant-selects>, or the
 *                             beans boxed-picker radios
 *     purchase-type cards  -> Appstle's input[name="selling_plan"] radios
 *     quantity stepper     -> the theme's <quantity-input> buttons
 *     Add to cart / sticky -> form.requestSubmit() on the main product form
 *
 * so the theme's own variant flow (URL, price block, media, Appstle, cart
 * submission) stays in charge and the submitted `id`, `quantity` and
 * `selling_plan` are exactly what they would be today.
 *
 * Analytics fire for BOTH arms so the experiment can be read. Plain
 * <script defer>, no imports — matches the rest of assets/ (see
 * collection-cro.js). Every handler is wrapped so a failure degrades to "no
 * variant behaviour", never a broken product page.
 */
(function () {
  var CFG = window.GimokaPDP;
  if (!CFG) return;

  var root = document.documentElement;
  var MOBILE = '(max-width: 749px)';
  var CART_ADD_RE = /\/cart\/add/;

  var state = {
    variant: 'control',
    inVariant: false,
    viewed: false,
    data: null,
    purchase: 'one_time', // 'one_time' | 'subscription'
    planId: null,
    pendingPurchase: null, // choice made before Appstle mounted
    lastSource: 'buy_box', // buy_box | sticky | upsell — for pdp_add_to_cart
    stickyShown: false,
    variantWired: false
  };

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */

  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }
  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }
  function setText(sel, text, ctx) {
    $$(sel, ctx).forEach(function (el) {
      if (el.textContent !== text) el.textContent = text;
    });
  }
  function show(el, visible) {
    if (!el) return;
    if (visible) el.removeAttribute('hidden');
    else el.setAttribute('hidden', '');
  }
  function isMobile() {
    try {
      return window.matchMedia(MOBILE).matches;
    } catch (e) {
      return false;
    }
  }

  // Every event carries the same four properties so the experiment can be
  // filtered by product / layout family while sharing one event schema.
  function track(name, props) {
    try {
      if (!window.posthog || typeof window.posthog.capture !== 'function') return;
      var payload = {
        experiment: CFG.experiment || 'pdp',
        variant: state.variant,
        product_handle: CFG.handle,
        template: CFG.template
      };
      if (props) {
        for (var k in props) {
          if (Object.prototype.hasOwnProperty.call(props, k)) payload[k] = props[k];
        }
      }
      window.posthog.capture(name, payload);
    } catch (e) {}
  }

  // shop.money_format ("£{{amount}}", "{{amount_with_comma_separator}} €", …)
  function formatMoney(cents) {
    var format = String(CFG.moneyFormat || '£{{amount}}').replace(/<[^>]+>/g, '');
    var value = (Number(cents) || 0) / 100;
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = match ? match[1] : 'amount';

    function fixed(n, decimals, thousands, decimal) {
      var parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    }

    var out;
    switch (key) {
      case 'amount_no_decimals':
        out = fixed(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        out = fixed(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = fixed(value, 0, '.', ',');
        break;
      default:
        out = fixed(value, 2, ',', '.');
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, out);
  }

  /* ------------------------------------------------------------------ *
   * Product data (snippets/pdp-cro-data.liquid) + the real form controls
   * ------------------------------------------------------------------ */

  function loadData() {
    var el = $('[data-pdp-data]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function productForm() {
    return state.data ? document.getElementById(state.data.formId) : null;
  }
  function variantIdInput() {
    var form = productForm();
    return form ? form.querySelector('input[name="id"]') : null;
  }
  function currentVariant() {
    if (!state.data) return null;
    var input = variantIdInput();
    var id = input && input.value ? Number(input.value) : state.data.defaultVariantId;
    for (var i = 0; i < state.data.variants.length; i++) {
      if (state.data.variants[i].id === id) return state.data.variants[i];
    }
    return null;
  }
  function quantityInput() {
    return (
      (state.data && document.getElementById('Quantity-' + state.data.sectionId)) ||
      $('product-info .quantity__input')
    );
  }
  function currentQty() {
    var input = quantityInput();
    var n = input ? parseInt(input.value, 10) : 1;
    return n > 0 ? n : 1;
  }

  /* ------------------------------------------------------------------ *
   * Subscription (Appstle) — the widget is injected asynchronously into
   * .product-form__buttons; its radios are inside the product form, so
   * whichever one is checked is what gets submitted as selling_plan.
   * ------------------------------------------------------------------ */

  function appstleWidget() {
    return $('.product-form__buttons [id^="appstle_subscription_widget"]');
  }
  function appstleRadios() {
    var widget = appstleWidget();
    return widget ? $$('input[name="selling_plan"]', widget) : [];
  }
  function appstleRadio(kind) {
    var radios = appstleRadios();
    for (var i = 0; i < radios.length; i++) {
      var isSub = radios[i].value !== '';
      if ((kind === 'subscription') === isSub) return radios[i];
    }
    return null;
  }

  function syncPurchaseFromAppstle() {
    var radios = appstleRadios();
    if (!radios.length) return;
    var checked = null;
    for (var i = 0; i < radios.length; i++) if (radios[i].checked) checked = radios[i];
    var kind = checked && checked.value !== '' ? 'subscription' : 'one_time';
    state.purchase = kind;
    state.planId = kind === 'subscription' ? checked.value : null;
    paintPurchase();
  }

  function paintPurchase() {
    $$('[data-pdp-purchase]').forEach(function (btn) {
      var active = btn.getAttribute('data-pdp-purchase') === state.purchase;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function setPurchase(kind, fromUser) {
    var radio = appstleRadio(kind);
    if (radio) {
      state.pendingPurchase = null;
      if (!radio.checked) radio.click(); // label semantics: checks it and fires change for Appstle
      state.purchase = kind;
      state.planId = kind === 'subscription' ? radio.value : null;
    } else {
      // Appstle hasn't mounted yet — remember the choice and apply on mount.
      state.pendingPurchase = kind;
      state.purchase = kind;
      var box = $('[data-pdp-purchase-box]');
      state.planId = kind === 'subscription' && box ? box.getAttribute('data-plan-id') : null;
    }
    paintPurchase();
    render();
    if (fromUser) {
      track('pdp_purchase_type_changed', { purchase_type: kind, plan_id: state.planId || null });
    }
  }

  function watchAppstle() {
    var container = $('.product-form__buttons');
    if (!container) return;
    var onMount = function () {
      if (state.pendingPurchase) setPurchase(state.pendingPurchase, false);
      else syncPurchaseFromAppstle();
      // Appstle re-renders prices on variant change; keep our cards in step.
      var widget = appstleWidget();
      if (widget && !widget.__pdpObserved) {
        widget.__pdpObserved = true;
        widget.addEventListener('change', function (e) {
          if (e.target && e.target.name === 'selling_plan') syncPurchaseFromAppstle();
          render();
        });
      }
    };
    if (appstleWidget()) onMount();
    var mo = new MutationObserver(function () {
      if (appstleWidget()) {
        onMount();
        mo.disconnect();
      }
    });
    mo.observe(container, { childList: true, subtree: true });
    setTimeout(function () {
      mo.disconnect();
    }, 30000);
  }

  function selectedPlan(variant) {
    if (!variant || state.purchase !== 'subscription') return null;
    var plans = variant.plans || [];
    for (var i = 0; i < plans.length; i++) {
      if (String(plans[i].id) === String(state.planId)) return plans[i];
    }
    return plans[0] || null;
  }

  /* ------------------------------------------------------------------ *
   * Rendering — price row, pack cards, purchase prices, CTA, sticky
   * ------------------------------------------------------------------ */

  function ctaLabel(variant, plan) {
    var S = CFG.strings || {};
    if (!variant) return { label: S.unavailable || 'Unavailable', price: '', disabled: true };
    if (!variant.available) return { label: S.soldOut || 'Sold out', price: '', disabled: true };
    if (variant.price === 0) return { label: S.addFree || S.add || 'Add to cart', price: '', disabled: false };
    var unit = plan ? plan.price : variant.price;
    return {
      label: plan ? S.subscribe || 'Subscribe' : S.add || 'Add to cart',
      price: formatMoney(unit * currentQty()),
      disabled: false
    };
  }

  function paintCta(btn, cta) {
    if (!btn) return;
    btn.disabled = !!cta.disabled;
    setText('[data-pdp-cta-label]', cta.label, btn);
    setText('[data-pdp-cta-price]', cta.price, btn);
    show($('[data-pdp-cta-price]', btn), cta.price !== '');
    show($('[data-pdp-cta-sep]', btn), cta.price !== '');
  }

  // Keep the variant thumbnail row in step with the gallery's active item
  // (the theme switches it on variant change, prepending the variant image).
  function syncThumbs() {
    var active = $('product-info .product__media-item.is-active');
    var id = active ? active.getAttribute('data-media-id') : null;
    $$('[data-pdp-thumb]').forEach(function (thumb) {
      thumb.setAttribute('aria-current', id && thumb.getAttribute('data-target') === id ? 'true' : 'false');
    });
  }

  function render() {
    if (!state.data) return;
    syncThumbs();
    var variant = currentVariant();
    var qty = currentQty();
    var plan = selectedPlan(variant);

    // Quantity display
    setText('[data-pdp-qty-value]', String(qty));

    if (variant) {
      var unit = plan ? plan.price : variant.price;
      var total = unit * qty;
      var compare = plan ? variant.price : variant.compare_at_price;
      var hasCompare = compare && compare > unit;
      var isFree = variant.price === 0;

      // Price row
      setText('[data-pdp-price]', formatMoney(total));
      setText('[data-pdp-compare]', hasCompare ? formatMoney(compare * qty) : '');
      show($('[data-pdp-compare]'), !!hasCompare);
      setText('[data-pdp-unit]', variant.unit_label || '');
      show($('[data-pdp-unit]'), !!variant.unit_label && !isFree);
      show($('[data-pdp-free]'), isFree);

      // Pack cards
      $$('[data-pdp-pack]').forEach(function (btn) {
        var active = Number(btn.getAttribute('data-variant-id')) === variant.id;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      // Purchase-option prices (Appstle's own widget shows the same numbers)
      setText('[data-pdp-purchase-price="one_time"]', formatMoney(variant.price * qty));
      var subPlan = null;
      var plans = variant.plans || [];
      for (var i = 0; i < plans.length; i++) {
        if (String(plans[i].id) === String(state.planId)) subPlan = plans[i];
      }
      subPlan = subPlan || plans[0] || null;
      if (subPlan) {
        setText('[data-pdp-purchase-price="subscription"]', formatMoney(subPlan.price * qty));
        setText('[data-pdp-purchase-compare]', formatMoney(variant.price * qty));
        show($('[data-pdp-purchase-compare]'), subPlan.price < variant.price);
      }

      // Sticky meta: "<pack> · <price>"
      var metaEl = $('[data-pdp-sticky-meta]');
      if (metaEl) {
        var pack = state.data.hasOnlyDefaultVariant ? state.data.packContents || '' : variant.title || '';
        var tmpl = (CFG.strings && CFG.strings.stickyMeta) || '__PACK__ · __PRICE__';
        var priceText = isFree ? formatMoney(0) : formatMoney(total);
        metaEl.textContent = pack ? tmpl.replace('__PACK__', pack).replace('__PRICE__', priceText) : priceText;
      }
    }

    // CTA + sticky button
    var cta = ctaLabel(variant, plan);
    paintCta($('[data-pdp-add="buy_box"]'), cta);
    paintCta($('[data-pdp-add="sticky"]'), cta);
  }

  /* ------------------------------------------------------------------ *
   * Proxies
   * ------------------------------------------------------------------ */

  function selectPack(btn) {
    if (!state.data) return;
    var id = Number(btn.getAttribute('data-variant-id'));
    var variant = null;
    for (var i = 0; i < state.data.variants.length; i++) {
      if (state.data.variants[i].id === id) variant = state.data.variants[i];
    }
    if (!variant || !variant.available) return;

    var packs = btn.closest('[data-pdp-packs]');
    var picker = packs ? packs.getAttribute('data-pdp-picker') : 'pills';
    var radio = null;
    if (picker === 'boxed') {
      radio = $('.hd-boxed-variants input[type="radio"][value="' + id + '"]');
    } else {
      var radios = $$('variant-selects input[type="radio"]');
      for (var j = 0; j < radios.length; j++) {
        if (radios[j].value === btn.getAttribute('data-option-value')) radio = radios[j];
      }
    }
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    render();
    track('pdp_pack_size_selected', {
      variant_id: variant.id,
      pack: variant.option1,
      price: Number((variant.price / 100).toFixed(2))
    });
  }

  function stepQuantity(direction) {
    var input = quantityInput();
    if (!input) return;
    var wrapper = input.closest('quantity-input');
    var nativeBtn = wrapper && wrapper.querySelector('button[name="' + (direction === 'plus' ? 'plus' : 'minus') + '"]');
    if (nativeBtn) {
      nativeBtn.click(); // <quantity-input> enforces min / max / step and fires change
    } else {
      var next = currentQty() + (direction === 'plus' ? 1 : -1);
      input.value = String(Math.max(1, next));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    render();
  }

  var busyTimer = null;
  function setBusy(busy) {
    $$('[data-pdp-add]').forEach(function (btn) {
      if (busy) btn.setAttribute('aria-busy', 'true');
      else btn.removeAttribute('aria-busy');
    });
    clearTimeout(busyTimer);
    if (busy) busyTimer = setTimeout(function () { setBusy(false); }, 10000);
  }

  function submitForm(source) {
    var form = productForm();
    if (!form) return;
    var submit = form.querySelector('[type="submit"]');
    if (submit && (submit.disabled || submit.getAttribute('aria-disabled') === 'true')) return;
    state.lastSource = source;
    setBusy(true);
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }

  function wireProxies() {
    document.addEventListener('click', function (event) {
      var t = event.target;
      if (!t || !t.closest) return;

      var pack = t.closest('[data-pdp-pack]');
      if (pack) {
        event.preventDefault();
        selectPack(pack);
        return;
      }

      var purchase = t.closest('[data-pdp-purchase]');
      if (purchase) {
        event.preventDefault();
        setPurchase(purchase.getAttribute('data-pdp-purchase'), true);
        return;
      }

      var qty = t.closest('[data-pdp-qty]');
      if (qty) {
        event.preventDefault();
        stepQuantity(qty.getAttribute('data-pdp-qty'));
        return;
      }

      var add = t.closest('[data-pdp-add]');
      if (add) {
        event.preventDefault();
        if (add.hasAttribute('aria-busy')) return;
        submitForm(add.getAttribute('data-pdp-add'));
        return;
      }

      // Cross-sell card (snippets/product-you-may-also-like.liquid) — adds via
      // its own fetch; only the attribution differs.
      if (t.closest('.ymal__add')) state.lastSource = 'upsell';

      var details = t.closest('[data-pdp-details-toggle]');
      if (details) {
        var expanded = details.getAttribute('aria-expanded') === 'true';
        details.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        var panel = $('[data-pdp-details-panel]', details.parentElement);
        show(panel, !expanded);
        return;
      }

      var machine = t.closest('[data-pdp-machine]');
      if (machine) {
        var box = machine.closest('[data-pdp-machine-box]');
        $$('[data-pdp-machine]', box).forEach(function (b) {
          b.setAttribute('aria-pressed', b === machine ? 'true' : 'false');
        });
        var fits = machine.getAttribute('data-fits') === 'true';
        var result = $('[data-pdp-machine-result]', box);
        if (result) {
          result.setAttribute('data-fits', fits ? 'true' : 'false');
          setText('[data-pdp-machine-message]', machine.getAttribute('data-message') || '', result);
          show(result, true);
        }
        track('pdp_machine_checked', { machine: machine.textContent.trim(), fits: fits });
        return;
      }

      var thumb = t.closest('[data-pdp-thumb]');
      if (thumb) {
        event.preventDefault();
        var gallery = $('product-info media-gallery');
        if (gallery && typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(thumb.getAttribute('data-target'), false);
        }
        syncThumbs();
        return;
      }

      var write = t.closest('[data-pdp-write-review]');
      if (write) {
        var link = $('.jdgm-write-rev-link');
        if (link) link.click();
        return;
      }

      var more = t.closest('[data-pdp-reviews-more]');
      if (more) {
        toggleReviews(more);
      }
    });

    // The real variant id input is the single source of truth: product-info.js
    // (pills) and the beans boxed picker both write it and fire change.
    document.addEventListener('change', function (event) {
      var t = event.target;
      if (!t || !state.data) return;
      var form = t.closest ? t.closest('form') : null;
      if (t.name === 'id' && form && form.id === state.data.formId) render();
      else if (t.classList && t.classList.contains('quantity__input')) render();
      else if (t.name === 'selling_plan') syncPurchaseFromAppstle();
    });

    // Belt and braces: the theme also announces variant changes on its bus.
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, function () {
          setTimeout(render, 0);
        });
        subscribe(PUB_SUB_EVENTS.quantityUpdate, function () {
          setTimeout(render, 0);
        });
      }
    } catch (e) {}
  }

  /* ------------------------------------------------------------------ *
   * Sticky add-to-cart bar (variant only)
   * ------------------------------------------------------------------ */

  function wireSticky() {
    var cta = $('[data-pdp-cta]');
    var bar = $('[data-pdp-sticky]');
    if (!cta || !bar || !('IntersectionObserver' in window)) return;
    // html.pdp--sticky-visible + --pdp-sticky-height let the CSS lift the
    // app launchers (BON Loyalty, Shopify Inbox) clear of the bar on mobile.
    function syncHeight() {
      root.style.setProperty('--pdp-sticky-height', bar.offsetHeight + 'px');
    }
    syncHeight();
    if ('ResizeObserver' in window) new ResizeObserver(syncHeight).observe(bar);
    var io = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      if (!entry) return;
      var visible = state.inVariant && !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', visible);
      root.classList.toggle('pdp--sticky-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (visible && !state.stickyShown) {
        state.stickyShown = true;
        track('pdp_sticky_shown');
      }
    }, { threshold: 0 });
    io.observe(cta);
    state.stickyObserver = io;
  }

  /* ------------------------------------------------------------------ *
   * Trust row carousel — mobile, variant only. One multicolumn item at a
   * time, auto-advancing every 3s, with dots. Nothing is moved: CSS hides
   * the items that are not .is-active on small screens.
   * ------------------------------------------------------------------ */

  function wireTrustCarousel() {
    var list = $('.multicolumn-list');
    if (!list || list.__pdpCarousel) return;
    var items = $$('.multicolumn-list__item', list);
    if (items.length < 2) return;
    list.__pdpCarousel = true;

    var section = list.closest('.multicolumn');
    var host = list.closest('.page-width') || section;
    var index = 0;
    var timer = null;

    var dots = document.createElement('div');
    dots.className = 'pdp-cro-trust-dots';
    dots.setAttribute('role', 'tablist');
    items.forEach(function (_, i) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'pdp-cro-trust-dots__dot';
      dot.setAttribute('aria-label', String(i + 1));
      dot.addEventListener('click', function () {
        go(i);
        restart();
      });
      dots.appendChild(dot);
    });
    host.appendChild(dots);

    function go(i) {
      index = (i + items.length) % items.length;
      items.forEach(function (item, j) {
        item.classList.toggle('is-active', j === index);
      });
      $$('.pdp-cro-trust-dots__dot', dots).forEach(function (dot, j) {
        dot.classList.toggle('is-active', j === index);
      });
    }
    function restart() {
      clearInterval(timer);
      timer = setInterval(function () {
        if (state.inVariant && isMobile()) go(index + 1);
      }, 3000);
    }

    go(0);
    restart();
    if (section) section.classList.add('pdp-cro-trust-ready');
  }

  /* ------------------------------------------------------------------ *
   * Reviews — Judge.me renders the cards; the design shows three (two on
   * mobile), so the rest sit behind a "Show all N reviews" toggle rather
   * than being cut off. Judge.me sets the widget up asynchronously.
   * ------------------------------------------------------------------ */

  function toggleReviews(btn) {
    var widget = $('.jdgm-rev-widg');
    if (!widget) return;
    var expanded = widget.classList.toggle('is-expanded');
    btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    var S = CFG.strings || {};
    var count = btn.getAttribute('data-count') || '';
    btn.textContent = expanded ? S.showFewerReviews || 'Show fewer reviews' : (S.showAllReviews || 'Show all __COUNT__ reviews').replace('__COUNT__', count);
  }

  function wireReviews() {
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      var widget = $('.jdgm-rev-widg');
      var list = widget && $('.jdgm-rev-widg__reviews', widget);
      var reviews = list ? $$('.jdgm-rev', list) : [];
      if (reviews.length || tries > 40) clearInterval(timer);
      if (!reviews.length || widget.__pdpMore) return;
      var limit = isMobile() ? 2 : 3;
      if (reviews.length <= limit) return;
      widget.__pdpMore = true;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pdp-cro-reviews__more';
      btn.setAttribute('data-pdp-reviews-more', '');
      btn.setAttribute('data-count', String(reviews.length));
      btn.setAttribute('aria-expanded', 'false');
      btn.textContent = ((CFG.strings && CFG.strings.showAllReviews) || 'Show all __COUNT__ reviews').replace('__COUNT__', String(reviews.length));
      list.parentNode.insertBefore(btn, list.nextSibling);
    }, 250);
  }

  /* ------------------------------------------------------------------ *
   * FAQ tracking — <details> rows exist in both arms; only the layout differs.
   * ------------------------------------------------------------------ */

  function wireFaqTracking() {
    $$('.collapsible-content details').forEach(function (details) {
      details.addEventListener('toggle', function () {
        if (!details.open) return;
        var title = details.querySelector('.accordion__title, summary');
        track('pdp_faq_opened', { question: title ? title.textContent.trim().slice(0, 200) : '' });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Cart watching — the storefront cart is the SlideCart HQ app, whose adds
   * never touch the theme's pubsub, so watch fetch/XHR for /cart/add (same
   * approach as assets/free-gift.js and collection-cro.js). Fires in BOTH
   * arms: this is the primary metric.
   * ------------------------------------------------------------------ */

  function trackAddFromResponse(payload) {
    if (!payload) return;
    var items = payload.items || (payload.id ? [payload] : []);
    items.forEach(function (item) {
      var price = item.final_price != null ? item.final_price : item.price;
      track('pdp_add_to_cart', {
        variant_id: item.variant_id || item.id,
        product_handle_added: item.handle || item.product_handle || null,
        price: Number(((price || 0) / 100).toFixed(2)),
        qty: item.quantity || 1,
        purchase_type: item.selling_plan_allocation ? 'subscription' : 'one_time',
        source: state.lastSource
      });
    });
    state.lastSource = 'buy_box';
    setBusy(false);
  }

  function watchCart() {
    try {
      var nativeFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : input && input.url;
        var isAdd = url && CART_ADD_RE.test(url);
        var call = nativeFetch.apply(this, arguments);
        if (!isAdd) return call;
        return call.then(
          function (response) {
            if (response.ok) {
              response
                .clone()
                .json()
                .then(trackAddFromResponse)
                .catch(function () {
                  setBusy(false);
                });
            } else {
              setBusy(false);
            }
            return response;
          },
          function (err) {
            setBusy(false);
            throw err;
          }
        );
      };
    } catch (e) {}

    try {
      var open = XMLHttpRequest.prototype.open;
      var send = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__pdpCartAdd = !!(url && CART_ADD_RE.test(url));
        return open.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function () {
        if (this.__pdpCartAdd) {
          this.addEventListener('load', function () {
            try {
              if (this.status >= 200 && this.status < 300) trackAddFromResponse(JSON.parse(this.responseText));
              else setBusy(false);
            } catch (e) {
              setBusy(false);
            }
          });
          this.addEventListener('error', function () {
            setBusy(false);
          });
        }
        return send.apply(this, arguments);
      };
    } catch (e) {}
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  function wireVariantOnly() {
    if (state.variantWired) return;
    state.variantWired = true;
    try {
      wireSticky();
    } catch (e) {}
    try {
      wireTrustCarousel();
    } catch (e) {}
    try {
      wireReviews();
    } catch (e) {}
  }

  // Called on every resolution: the first definitive one fires the pageview
  // companion event; a later flip (console override) re-applies behaviour.
  function onResolved(variant, definitive) {
    state.variant = variant;
    state.inVariant = variant !== 'control';

    if (definitive && !state.viewed) {
      state.viewed = true;
      // Both arms, once per pageview. PostHog records the exposure itself via
      // getFeatureFlag ($feature_flag_called); this is the page-level companion
      // so control and variant pageviews are comparable.
      track('pdp_variant_viewed');
    }

    if (state.inVariant) {
      wireVariantOnly();
      render();
    } else {
      var bar = $('[data-pdp-sticky]');
      if (bar) bar.classList.remove('is-visible');
      root.classList.remove('pdp--sticky-visible');
    }
  }

  function boot() {
    try {
      state.data = loadData();
      wireProxies();
      watchAppstle();
      wireFaqTracking();
      watchCart();
      render();

      document.addEventListener('gimoka:pdp:resolved', function (event) {
        onResolved(event.detail.variant, event.detail.definitive === true);
      });

      // The config runs in <head> before this deferred file, so it has usually
      // resolved already and the event has been and gone — pick it up from <html>.
      var resolved = root.getAttribute('data-pdp-variant');
      if (resolved) onResolved(resolved, root.getAttribute('data-pdp-definitive') === 'true');
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
